import { useCallback, useEffect, useState } from 'react';

import { Flex, Space } from '@/components/base';
import ContentPreview from '@/components/Preview/ContentPreview';
import Typo from '@/components/Typography/Typo';
import useAgentStore from '@/features/Agent/stores';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useLocaleStore from '@/stores/LocaleStore';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import S from './styled';
import type { CompanyKeyT, Props, RefineDataT } from './types';

const data: CompanyKeyT[] = [
  'created_at',
  'last_activity',
  'feedback_count',
  'employee_count',
  'annual_revenue',
  'agent',
  'industry',
  'size',
  'plan',
  'rating',
  'type',
  'country',
];

const filterDataKey = new Set(data);

const FeedbackCompanyPreview = ({ alias, name, pos }: Props) => {
  const [data, setData] = useState<CompanyT | null>(null);
  const regionMap = useLocaleStore((state) => state.regionMap);
  const agentMap = useAgentStore((state) => state.agentMap);

  const color = useColor();

  const fetchData = useCallback(async () => {
    const res = await service.account.fetchCompany(alias);
    setData(res);
  }, [alias]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const COMPANY_DATA_T: RefineDataT = {
    employee_count: {
      title: 'employee',
    },
    annual_revenue: {
      title: 'annual revenue',
    },
    feedback_count: {
      title: 'feedback',
    },
    status: {
      title: 'active status',
    },
    created_at: {
      title: 'create date',
      handleData: (data: string) =>
        getTimezoneDayjs(data).format(DATE_FORMAT.readable),
    },
    last_activity: {
      title: 'last activity date',
      handleData: (data: string) =>
        getTimezoneDayjs(data).format(DATE_FORMAT.readable),
    },
    country: {
      title: 'country',
      handleData: (data: string) => regionMap[data].country,
    },
    agent: {
      title: 'agent',
      handleData: (data: string) => agentMap[data].name,
    },
  };

  return (
    <>
      {data ? (
        <ContentPreview style={pos}>
          <S.Style>
            <Typo type="body2" weight="sb">
              {name}
            </Typo>
            <Flex g="8px" w="wrap">
              <Space h="16px" />
              {Object.keys(data)
                .filter((item) => filterDataKey.has(item))
                .map((key: CompanyKeyT) => {
                  return (
                    <S.ItemWrapper g="6px" key={`${key}`}>
                      <Typo type="body3" color={color.gray600}>
                        {COMPANY_DATA_T[key as string]
                          ? COMPANY_DATA_T[key as string].title
                          : key}
                      </Typo>
                      <Typo type="body3" color={color.gray800}>
                        {COMPANY_DATA_T[key as string] &&
                        COMPANY_DATA_T[key as string].handleData
                          ? COMPANY_DATA_T[key as string].handleData?.(
                              data[key as keyof CompanyT] as string,
                            )
                          : data[key as keyof CompanyT]}
                      </Typo>
                    </S.ItemWrapper>
                  );
                })}
            </Flex>
          </S.Style>
        </ContentPreview>
      ) : (
        <></>
      )}
    </>
  );
};

export default FeedbackCompanyPreview;
